import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Phone, ArrowRight, Zap, Gauge, Mountain, SprayCan, Disc3, Wrench } from "lucide-react";
import { Lightning } from "../components/Lightning";
import { Marquee } from "../components/Marquee";
import { MaskedLine, FadeUp, SectionHeading } from "../components/Reveal";
import { ServiceCard } from "../components/ServiceCard";
import { IMAGES, NEW_IMAGES, PHONE, PHONE_HREF, SERVICES } from "../site";
import { SpinBadge, TreadDivider } from "../components/Flourishes";
import { Reviews } from "../components/Reviews";

const HERO_IMAGES = [IMAGES.gmc, NEW_IMAGES.blackFordRed, NEW_IMAGES.blueF250, NEW_IMAGES.jeepRear, NEW_IMAGES.denaliPurple];

const SERVICE_ICONS = { diesel: Gauge, lifts: Mountain, lighting: Zap, wheels: Disc3, body: SprayCan };

const CHAPTERS = [
  {
    num: "01",
    heading: "Diesel Performance",
    desc: "Heavy-duty diagnostics, engine bay overhauls, turbo upgrades and mechanical servicing. If it burns diesel, we make it burn better.",
  },
  {
    num: "02",
    heading: "Stance, Lifts & Suspension",
    desc: "Custom suspension lift kits, leveling systems, reservoir shocks and high-clearance setups designed for command — on the road and off it.",
  },
  {
    num: "03",
    heading: "Lighting & Underglow",
    desc: "RGBW underglow, high-intensity wheel ring lights, accent strips and headlight work engineered for total night dominance.",
  },
  {
    num: "04",
    heading: "Paint Match & Body Repair",
    desc: "Paint matching, body repair, custom bumpers, trim and badges — color-matched and finished in-house.",
  },
];

const Hero = () => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 220]);
  const fade = useTransform(scrollY, [0, 550], [1, 0.15]);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 7000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative flex min-h-svh items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          <motion.img
            key={heroIdx}
            src={HERO_IMAGES[heroIdx]}
            alt="Custom truck build by ETN Customz"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="absolute inset-0 h-[118%] w-full object-cover object-[center_30%]"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/55 to-[#050505]/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-transparent" />
      </motion.div>

      <Lightning className="pointer-events-none absolute inset-0 h-full w-full" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-8 pb-16 sm:pb-24 pt-40">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="clip-badge mb-6 inline-flex items-center gap-2 border border-bolt/50 bg-bolt/15 px-4 py-1.5"
        >
          <Zap size={13} className="text-bolt-bright" fill="currentColor" />
          <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-bolt-bright">
            Eastern Tennessee · Diesel & Custom Truck Shop
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase italic tracking-tighter leading-[0.85]" data-testid="hero-headline">
          <MaskedLine delay={0.25} className="text-6xl sm:text-8xl lg:text-[9rem] text-white">
            Eastern TN
          </MaskedLine>
          <MaskedLine delay={0.42} className="text-6xl sm:text-8xl lg:text-[9rem] text-volt glow-volt">
            Customz
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-6 max-w-xl font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-zinc-300 leading-loose"
        >
          Lifts · Lighting · Wheels · Tires · Accessories · Diesel Mechanic
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-quote-button"
            className="clip-badge group inline-flex items-center gap-2 bg-volt px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-black transition-all duration-300 hover:bg-volt-soft hover:gap-3.5"
          >
            Request a build quote
            <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
          <a
            href={PHONE_HREF}
            data-testid="hero-call-button"
            className="clip-badge inline-flex items-center gap-2 border border-zinc-600 bg-black/50 px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-white backdrop-blur transition-colors duration-300 hover:border-bolt hover:text-bolt-bright"
          >
            <Phone size={18} />
            {PHONE}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="absolute bottom-8 right-8 z-10 hidden lg:block h-28 w-28"
      >
        <SpinBadge className="h-full w-full" />
      </motion.div>
    </section>
  );
};

const Manifesto = () => (
  <section id="manifesto" className="relative mx-auto max-w-7xl px-4 sm:px-8 py-24 sm:py-36" data-testid="manifesto-section">
    <SectionHeading eyebrow="The Manifesto" title={<>What we do.<br /><span className="text-outline-volt">Done loud.</span></>} className="mb-16" />
    <div>
      {CHAPTERS.map((c, i) => (
        <FadeUp key={c.num} delay={i * 0.06}>
          <div
            data-testid={`manifesto-chapter-${c.num}`}
            className="group grid grid-cols-[auto_1fr] sm:grid-cols-[120px_1fr_1.2fr] items-baseline gap-x-6 gap-y-3 border-t border-zinc-800 py-10 transition-colors duration-500 hover:border-volt/40"
          >
            <span className="font-display text-5xl sm:text-7xl font-black italic text-outline-bolt transition-colors duration-500 group-hover:text-volt group-hover:[-webkit-text-stroke:0px]">
              {c.num}
            </span>
            <h3 className="font-display text-2xl sm:text-4xl font-bold uppercase tracking-tight text-white">
              {c.heading}
            </h3>
            <p className="col-span-2 sm:col-span-1 text-sm sm:text-base leading-relaxed text-zinc-400">
              {c.desc}
            </p>
          </div>
        </FadeUp>
      ))}
      <div className="border-t border-zinc-800" />
    </div>
  </section>
);

const ServicesPreview = () => (
  <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-24 sm:pb-36" data-testid="services-preview-section">
    <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
      <SectionHeading eyebrow="Capabilities" title="Built. Lifted. Lit." />
      <FadeUp delay={0.15}>
        <Link
          to="/services"
          data-testid="services-preview-view-all"
          className="group inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-volt hover:text-volt-soft transition-colors"
        >
          All services <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {SERVICES.slice(0, 4).map((s, i) => (
        <ServiceCard key={s.id} service={s} icon={SERVICE_ICONS[s.id]} index={i} showImage={false} />
      ))}
    </div>
  </section>
);

const GalleryStrip = () => {
  const shots = [
    { src: IMAGES.ford, label: "Super Duty · Wheel Lights" },
    { src: IMAGES.gmc, label: "Denali · Underglow" },
    { src: IMAGES.chevy, label: "Z71 · Lifted Stance" },
    { src: IMAGES.engine, label: "SDP · Engine Bay" },
    { src: NEW_IMAGES.paintBooth, label: "Paint Match · Fresh Out the Booth" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-8 pb-24 sm:pb-36" data-testid="gallery-strip-section">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <SectionHeading eyebrow="Recent Builds" title="Proof, not promises" />
        <FadeUp delay={0.15}>
          <Link
            to="/gallery"
            data-testid="gallery-strip-view-all"
            className="group inline-flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-volt hover:text-volt-soft transition-colors"
          >
            Full gallery <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {shots.map((s, i) => (
          <FadeUp key={s.label} delay={i * 0.08}>
            <Link to="/gallery" className="clip-corner hud-frame group relative block aspect-[3/4] overflow-hidden border border-zinc-800" data-testid={`gallery-strip-item-${i}`}>
              <img src={s.src} alt={s.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
              <p className="absolute bottom-4 left-4 right-4 font-mono text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-volt">
                {s.label}
              </p>
            </Link>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

const CtaBand = () => (
  <section className="relative overflow-hidden border-t border-zinc-800" data-testid="cta-band-section">
    <img src={IMAGES.chevy} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover object-center opacity-25" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/60" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-8 py-24 sm:py-32">
      <FadeUp>
        <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-bolt-bright mb-5">
          Ready when you are
        </p>
        <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black uppercase italic tracking-tighter leading-[0.9] text-white max-w-3xl">
          Your truck.<br /><span className="text-volt glow-volt">Our obsession.</span>
        </h2>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="cta-band-quote-button"
            className="clip-badge inline-flex items-center gap-2 bg-volt px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-black transition-colors duration-300 hover:bg-volt-soft"
          >
            Start your build <Wrench size={18} />
          </Link>
          <a
            href={PHONE_HREF}
            data-testid="cta-band-call-button"
            className="clip-badge inline-flex items-center gap-2 border border-zinc-600 bg-black/50 px-8 py-4 font-display text-xl font-bold uppercase tracking-wide text-white backdrop-blur transition-colors duration-300 hover:border-bolt hover:text-bolt-bright"
          >
            <Phone size={18} /> {PHONE}
          </a>
        </div>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Manifesto />
      <TreadDivider />
      <ServicesPreview />
      <GalleryStrip />
      <Reviews />
      <TreadDivider />
      <CtaBand />
    </main>
  );
}
